// One white section card for the full-screen builders' form pane (KAN-763
// polish): a 32px brand-muted icon tile + title + a one-line muted subtitle,
// with an optional right-aligned status word. The status is plain text in a
// tone colour — never a pill — so three stacked cards read as a calm
// checklist: done (success) · to do (warning) · optional (muted) · problem
// (danger). Presentation only: the caller decides the label and the tone.
import { ChevronRight } from 'lucide-react';
import { foundation } from '../../../ui';
import { wizardIconTileStyle } from '../wizard/wizardChromeStyles';
import { useGroupedEdge, FLUSH_BODY_BOTTOM, FlushInsetContext } from './flushInset';

const fc = foundation.color;
const fs = foundation.fontSize;
const fw = foundation.fontWeight;
const { spacePx } = foundation;

/** Status tone -> text colour. Exported so tests / callers pin one mapping. */
export const SECTION_STATUS_COLOR = {
  done: fc.semantic.success,
  todo: fc.semantic.warningStrong,
  optional: fc.neutral.textMuted,
  problem: fc.semantic.danger,
};

const S = {
  card: {
    background: fc.neutral.surface,
    border: `1px solid ${fc.neutral.border}`,
    borderRadius: foundation.radii.lg,
    boxShadow: foundation.shadow.sm,
    minWidth: 0,
  },
  head: (edge, touch) => ({
    display: 'flex',
    alignItems: 'center',
    gap: spacePx[3],
    padding: `${touch ? spacePx[4] : spacePx[5]} ${edge}px ${spacePx[1]}`,
  }),
  titles: { minWidth: 0, flex: 1 },
  title: { margin: 0, fontSize: fs.md, fontWeight: fw.semibold, color: fc.neutral.text, lineHeight: 1.25, letterSpacing: '-0.01em' },
  subtitle: {
    margin: `2px 0 0`,
    fontSize: fs.xs,
    color: fc.neutral.textSecondary,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  subtitleWrap: { margin: `2px 0 0`, fontSize: fs.xs, color: fc.neutral.textSecondary, lineHeight: 1.4 },
  // Same look as lineItemKitStyles.figure: a bold total at the right end of the header.
  figure: {
    flexShrink: 0,
    fontSize: fs.base,
    fontWeight: fw.bold,
    color: fc.neutral.text,
    fontVariantNumeric: 'tabular-nums',
    whiteSpace: 'nowrap',
  },
  status: (tone) => ({
    flexShrink: 0,
    fontSize: fs.sm,
    fontWeight: fw.semibold,
    color: SECTION_STATUS_COLOR[tone] || fc.neutral.textMuted,
    whiteSpace: 'nowrap',
  }),
  // Opt-in clickable header (`onHeaderClick`): the whole head row is ONE button, 44px tall under a finger.
  headButton: (touch, disabled) => ({
    width: '100%',
    boxSizing: 'border-box',
    minHeight: touch ? 44 : undefined,
    border: 'none',
    background: 'transparent',
    textAlign: 'left',
    font: 'inherit',
    color: 'inherit',
    cursor: disabled ? 'not-allowed' : 'pointer',
    opacity: disabled ? 0.55 : 1,
    WebkitTapHighlightColor: 'transparent',
    borderRadius: `${foundation.radii.lg} ${foundation.radii.lg} 0 0`,
  }),
  chevron: { flexShrink: 0, color: fc.neutral.iconMuted },
  body: (touch, flush, edge) => ({
    display: 'flex',
    flexDirection: 'column',
    gap: spacePx[4],
    padding: flush ? `${spacePx[3]} 0 ${FLUSH_BODY_BOTTOM}` : `${touch ? spacePx[3] : spacePx[4]} ${edge}px ${touch ? spacePx[4] : spacePx[5]}`,
  }),
};

/**
 * @param {object} props
 * @param {import('react').ReactNode} props.icon - a lucide glyph element, rendered in the brand tile.
 * @param {string} props.title
 * @param {string} [props.subtitle]
 * @param {{ tone: 'done'|'todo'|'optional'|'problem', label: string } | null} [props.status] - none = no status text.
 * @param {import('react').ReactNode} [props.figure] - optional bold figure (a total) at the right end of the header, before any status.
 * @param {boolean} [props.touch] - phone / tablet spacing.
 * @param {boolean} [props.invalid] - opt-in: tint the card border with the danger token (a whole-section problem).

 * @param {boolean} [props.wrapSubtitle] - opt-in: the subtitle wraps instead of one ellipsised line.
 * @param {boolean} [props.flush] - opt-in: no side/bottom padding in the body; the children draw their own inset (edge-to-edge rows).
 * @param {() => void} [props.onHeaderClick] - opt-in: the whole header row becomes one button with a trailing chevron (open the record).
 * @param {string} [props.headerLabel] - accessible name of that button.
 * @param {boolean} [props.headerDisabled] - the header button is inert (a save is running).
 * @param {string} [props.headerTestId]
 * @param {string} [props.testId]
 * @param {import('react').ReactNode} props.children - the card body fields.
 */
export default function FullScreenSectionCard({
  icon, title, subtitle, wrapSubtitle = false, status = null, figure = null, touch = false, invalid = false, flush = false, testId,
  onHeaderClick, headerLabel, headerDisabled = false, headerTestId, children,
}) {
  const edge = useGroupedEdge(touch);
  const statusNode = status ? (
    <span
      style={S.status(status.tone)}
      data-testid={status.testId || (testId ? `${testId}-status` : undefined)}
      data-tone={status.tone}
      aria-label={status.ariaLabel}
    >
      {status.label}
    </span>
  ) : null;
  const figureNode = figure != null && figure !== false ? <span style={S.figure} data-testid={testId ? `${testId}-figure` : undefined}>{figure}</span> : null;
  const clickable = typeof onHeaderClick === 'function';
  const titles = (
    <div style={S.titles}>
      {clickable ? <span style={{ ...S.title, display: 'block' }}>{title}</span> : <h3 style={S.title}>{title}</h3>}
      {subtitle ? <p style={wrapSubtitle ? S.subtitleWrap : S.subtitle}>{subtitle}</p> : null}
    </div>
  );
  return (
    <section style={invalid ? { ...S.card, borderColor: fc.semantic.danger } : S.card} data-testid={testId}>
      {clickable ? (
        <button
          type="button"
          style={{ ...S.head(edge, touch), ...S.headButton(touch, headerDisabled) }}
          onClick={() => { if (!headerDisabled) onHeaderClick(); }}
          aria-disabled={headerDisabled || undefined}
          aria-label={headerLabel}
          data-testid={headerTestId}
        >
          <span style={wizardIconTileStyle} aria-hidden>{icon}</span>
          {titles}
          {figureNode}
          {statusNode}
          <ChevronRight size={18} aria-hidden style={S.chevron} />
        </button>
      ) : (
        <div style={S.head(edge, touch)}>
          <span style={wizardIconTileStyle} aria-hidden>{icon}</span>
          {titles}
          {figureNode}
          {statusNode}
        </div>
      )}
      <FlushInsetContext.Provider value={edge}><div style={S.body(touch, flush, edge)}>{children}</div></FlushInsetContext.Provider>
    </section>
  );
}
